// src/utils/subPayPayments.js
// ===========================================================================
// Subcontractor PAYMENTS for the job Sub Pay panel and the sub's My Pay view.
//
// Same collections and the same document shape Job Estimate Spreadsheet and
// Contractor Pay write, so a payment recorded on a job shows on every page:
//   subcontractorPay/{id}   the payment (jobId ties it to the job)
//   transactions/{txId}     the expense mirror the dashboards read
// Older payments in "subcontractorPayments" are read too (never written).
// ===========================================================================

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { buildContractorIdentities } from "./contractorIdentity";
import { applySubPayTerms, isContinuationJob, isOwnTerm, normalizeSubName, planDailySplit, termKey } from "./subPayTerms";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return "";
};

export const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const SUB_PAY_METHODS = ["Check", "ACH", "Cash", "Zelle", "Credit Card", "Other"];

/** Same normalizer Contractor Pay / Job Estimate Spreadsheet use. */
export const normalizePayment = (d, sourceCollection) => {
  const data = d.data();
  return {
    id: d.id,
    sourceCollection,
    subcontractorId: data.subcontractorId || data.userId || "",
    userId: data.userId || "",
    subcontractorName: data.subcontractorName || data.userName || data.vendor || data.property || "",
    amount: num(data.amount ?? data.amountPaid),
    date: toDateKey(data.date || data.datePaid || data.createdAt),
    method: data.method || "",
    reference: data.reference || data.checkNumber || "",
    note: data.note || data.description || "",
    jobId: data.jobId || "",
    occurrenceKey: data.occurrenceKey || "",
    jobLabel: data.jobLabel || "",
    sourcePage: data.sourcePage || "",
    txId: data.txId || "",
  };
};

/* ---- shared reads (cached for the session, refreshed on demand) ---------- */
let peopleCache = null;
export const loadPeople = async (force = false) => {
  if (peopleCache && !force) return peopleCache;
  const [usersSnap, contractorsSnap] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "contractors")),
  ]);
  const staff = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const contractorRecords = contractorsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const ids = buildContractorIdentities({ staff, contractorRecords, includeTerminated: true });
  peopleCache = { staff, contractorRecords, ...ids };
  return peopleCache;
};

/** Same contractor? Uses every name form a person has (person name, company...). */
export const makeSameSub = (people) => (a, b) => {
  if (!a || !b) return false;
  if (!people) return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  const ka = people.bucketKeyForName(a);
  return Boolean(ka) && ka === people.bucketKeyForName(b);
};

export const loadAllSubPayments = async () => {
  const [a, b] = await Promise.all([
    getDocs(collection(db, "subcontractorPay")),
    getDocs(collection(db, "subcontractorPayments")).catch(() => ({ docs: [] })),
  ]);
  return [
    ...a.docs.map((d) => normalizePayment(d, "subcontractorPay")),
    ...b.docs.map((d) => normalizePayment(d, "subcontractorPayments")),
  ];
};

/* ---- the job that holds a project price ---------------------------------- */
/**
 * A project price is owed once, on the job it was set on. For a continued job
 * walk back up the Continue chain to find it. Returns { id, wo } or null.
 */
export const findProjectJob = async (job, term) => {
  if (!job) return null;
  if (isOwnTerm(job, term)) return { id: job.id, wo: job.wo || "", job };
  if (term?.projectJobId) {
    const snap = await getDoc(doc(db, "jobs", term.projectJobId));
    if (snap.exists()) return { id: snap.id, wo: snap.data().wo || "", job: { id: snap.id, ...snap.data() } };
  }
  let cur = job;
  for (let i = 0; i < 40 && cur; i += 1) {
    const parentId = cur.projectContinuedFromJobId || cur.continuedFromJobId;
    if (!parentId) break;
    const snap = await getDoc(doc(db, "jobs", parentId));
    if (!snap.exists()) break;
    cur = { id: snap.id, ...snap.data() };
    const parentTerms = cur.subPayTerms || {};
    const t =
      parentTerms[termKey(term.name)] ||
      Object.values(parentTerms).find((x) => normalizeSubName(x?.name) === normalizeSubName(term.name)) ||
      null;
    if (t && isOwnTerm(cur, t)) return { id: cur.id, wo: cur.wo || "", job: cur };
    if (!isContinuationJob(cur)) return { id: cur.id, wo: cur.wo || "", job: cur };
  }
  return null;
};

/* ---- writes --------------------------------------------------------------- */
export const jobLabelFor = (job) =>
  job ? `${job.wo ? `WO ${job.wo}` : "No WO"} / ${job.siteName || "Job"}` : "";

/**
 * Record one payment against a job. person: { id (users doc id, optional), name }.
 * Writes the same fields as Job Estimate Spreadsheet's writeSubPayments.
 */
export const recordSubPayment = async ({ person, job, amount, date, method, reference, note }) => {
  const value = Math.round(num(amount) * 100) / 100;
  if (!(value > 0)) throw new Error("Enter an amount greater than $0.");
  const day = date || todayKey();
  const name = person.name;
  const jobLabel = jobLabelFor(job);
  const payload = {
    subcontractorId: String(person.id || name),
    userId: person.id || "",
    subcontractorName: name,
    userName: name,
    vendor: name,
    property: name,
    amount: value,
    amountPaid: value,
    date: day,
    datePaid: new Date(`${day}T12:00:00`),
    method: method || "Other",
    reference: reference || "",
    note: note || "",
    jobId: job?.id || "",
    occurrenceKey: "",
    jobLabel,
    payFrequency: "once",
    category: "Subcontractor",
    effect: "subtract",
    sourcePage: "JobSubPayPanel",
    createdAt: new Date().toISOString(),
  };
  const ref = await addDoc(collection(db, "subcontractorPay"), payload);
  const txRef = await addDoc(collection(db, "transactions"), {
    date: day,
    type: "expense",
    amount: value,
    category: "Subcontractor",
    vendor: name,
    memo: payload.note || `Subcontractor payment${payload.reference ? ` - ${payload.reference}` : ""}${jobLabel ? ` (${jobLabel})` : ""}`,
    jobId: job?.id || null,
    method: payload.method,
    sourceCollection: "subcontractorPay",
    sourceId: ref.id,
    updatedAt: new Date(),
  });
  await updateDoc(doc(db, "subcontractorPay", ref.id), { txId: txRef.id });
  return { id: ref.id, sourceCollection: "subcontractorPay", txId: txRef.id, ...payload };
};

/** Change amount / date / method / reference / note on a payment (and its mirror). */
export const updateSubPayment = async (payment, patch) => {
  const col = payment.sourceCollection || "subcontractorPay";
  const value = patch.amount !== undefined ? Math.round(num(patch.amount) * 100) / 100 : payment.amount;
  if (!(value > 0)) throw new Error("Enter an amount greater than $0.");
  const day = patch.date || payment.date || todayKey();
  const fields = {
    amount: value,
    amountPaid: value,
    date: day,
    datePaid: new Date(`${day}T12:00:00`),
    method: patch.method ?? payment.method ?? "Other",
    reference: patch.reference ?? payment.reference ?? "",
    note: patch.note ?? payment.note ?? "",
    updatedAt: new Date().toISOString(),
  };
  await updateDoc(doc(db, col, payment.id), fields);
  if (payment.txId) {
    await updateDoc(doc(db, "transactions", payment.txId), {
      date: day,
      amount: value,
      method: fields.method,
      updatedAt: new Date(),
    }).catch((e) => console.warn("Transaction mirror not updated:", e));
  }
  return { ...payment, ...fields };
};

export const deleteSubPayment = async (payment) => {
  const col = payment.sourceCollection || "subcontractorPay";
  await deleteDoc(doc(db, col, payment.id));
  if (payment.txId) {
    await deleteDoc(doc(db, "transactions", payment.txId)).catch((e) => console.warn("Transaction mirror not deleted:", e));
  }
};

/**
 * Continue: roll the sub pay terms over onto the copied job (before it is
 * saved). Daily keeps the crew and rate and counts the new job's days; a
 * project price stays owed on the first job only. Lines of subs WITHOUT
 * terms are left exactly as the page copied them.
 */
export const rollSubPayTermsOnto = async (copiedJob) => {
  if (!copiedJob?.subPayTerms || !Object.keys(copiedJob.subPayTerms).length) return copiedJob;
  try {
    const people = await loadPeople().catch(() => null);
    const result = applySubPayTerms(
      { ...copiedJob, id: undefined },
      { sameSub: makeSameSub(people), freshCopy: true }
    );
    if (!result.changed) return copiedJob;
    return { ...copiedJob, subcontractorPay: result.subcontractorPay, subPayTerms: result.subPayTerms };
  } catch (err) {
    console.warn("Sub pay roll-over skipped:", err);
    return copiedJob;
  }
};

/* ---- clean-up: copy a job's pay to the jobs continued from it ------------- */
/**
 * Every job continued from this one, and from those, and so on
 * (3101 -> 3101-2 -> 3101-3). Follows all three Continue links the app writes.
 */
export const findContinuedJobs = async (jobId, max = 80) => {
  const out = [];
  const seen = new Set([jobId]);
  const queue = [jobId];
  while (queue.length && out.length < max) {
    const id = queue.shift();
    const snaps = await Promise.all(
      ["continuedFromJobId", "projectContinuedFromJobId", "previousJobId"].map((f) =>
        getDocs(query(collection(db, "jobs"), where(f, "==", id))).catch(() => ({ docs: [] }))
      )
    );
    snaps.forEach((snap) =>
      snap.docs.forEach((d) => {
        if (seen.has(d.id)) return;
        seen.add(d.id);
        out.push({ id: d.id, ...d.data() });
        queue.push(d.id);
      })
    );
  }
  return out.sort((a, b) => String(a.startDate || "").localeCompare(String(b.startDate || "")));
};

/**
 * What copying `term` (set on rootJob) to each continued job would do.
 * Daily: same crew and rate, each job's own days. Project: shown on each job,
 * owed once on rootJob ($0 on the continued ones).
 * Returns [{ job, before, after, patch }] - nothing is written.
 */
export const planCopyToContinued = ({ rootJob, term, children, sameSub }) =>
  children.map((child) => {
    const terms = { ...(child.subPayTerms || {}) };
    Object.keys(terms).forEach((k) => {
      if (sameSub(terms[k]?.name || "", term.name)) delete terms[k];
    });
    terms[termKey(term.name)] = {
      name: term.name,
      mode: term.mode,
      workers: term.workers,
      ratePerWorker: term.ratePerWorker,
      days: "",
      projectAmount: term.projectAmount,
      setOnJobId: rootJob.id, // belongs to the first job: auto days, project not owed again
      projectJobId: term.mode === "project" ? rootJob.id : "",
      computedSig: "",
      copiedFromJobId: rootJob.id,
      updatedAt: new Date().toISOString(),
    };
    const lineBefore = (child.subcontractorPay || []).find((l) => sameSub(l?.subcontractorName || "", term.name));
    const result = applySubPayTerms({ ...child, subPayTerms: terms }, { sameSub });
    const lineAfter = result.subcontractorPay.find((l) => sameSub(l?.subcontractorName || "", term.name));
    const owed = (l) =>
      l ? Number(l.basePay || 0) + (l.changeOrders || []).reduce((s, c) => s + (Number(c?.amount) || 0), 0) : 0;
    return {
      job: child,
      before: owed(lineBefore),
      after: owed(lineAfter),
      patch: { subPayTerms: result.subPayTerms, subcontractorPay: result.subcontractorPay },
    };
  });

export const applyCopyToContinued = async (plan) => {
  for (const row of plan) {
    await updateDoc(doc(db, "jobs", row.job.id), { ...row.patch, subPayTermsUpdatedAt: new Date().toISOString() });
  }
};


/* ---- same-day split for Daily pay ---------------------------------------- */
/**
 * A sub on several Daily jobs the same day is owed ONE day's pay, divided
 * across those jobs ($350 / 5 jobs = $70 each). This finds every Daily job for
 * the given subs (all their name forms), works out the split with
 * planDailySplit() and writes the jobs that change.
 *   names   sub names (as typed on Tech Assign / terms)
 *   dryRun  true = return the plan only, write nothing
 * Returns { rows, patches } (see planDailySplit).
 */
export const rebalanceDailySplit = async (names = [], { dryRun = false } = {}) => {
  const list = Array.from(new Set(names.map((n) => String(n || "").trim()).filter(Boolean)));
  if (!list.length) return { rows: [], patches: {} };
  const people = await loadPeople().catch(() => null);
  const groupOf = (n) => (people ? people.bucketKeyForName(n) : normalizeSubName(n));
  const groups = new Set(list.map(groupOf).filter(Boolean));

  // every term key this person could be saved under
  const keys = new Set();
  list.forEach((n) => {
    keys.add(termKey(n));
    const identity = people && people.resolveIdentity ? people.resolveIdentity(n) : null;
    (identity?.aliasNames || []).forEach((a) => keys.add(termKey(a)));
  });

  const jobs = new Map();
  await Promise.all(
    Array.from(keys)
      .filter(Boolean)
      .map(async (key) => {
        const snap = await getDocs(query(collection(db, "jobs"), where(new FieldPath("subPayTerms", key, "mode"), "==", "daily"))).catch(
          (err) => {
            console.warn("Daily split: query failed for", key, err);
            return { docs: [] };
          }
        );
        snap.docs.forEach((d) => jobs.set(d.id, { id: d.id, ...d.data() }));
      })
  );

  const plan = planDailySplit(Array.from(jobs.values()), { groupOf, onlyGroups: groups });
  if (!dryRun) await writeDailySplit(plan);
  return plan;
};

/** Write a planDailySplit() result. */
export const writeDailySplit = async (plan) => {
  const ids = Object.keys(plan?.patches || {});
  for (const id of ids) {
    await updateDoc(doc(db, "jobs", id), { ...plan.patches[id], subPayTermsUpdatedAt: new Date().toISOString() });
  }
  return ids.length;
};

/* ---- Split Sub Payment: one check across several jobs -------------------- */
/**
 * Everything the Split Sub Payment popup needs, read once:
 *   { people, jobs, occurrences, payments }
 * Same sources as Contractor Pay (jobs + recurring_job_occurrences owed lines,
 * subcontractorPay + subcontractorPayments payments).
 */
export const loadSplitSubPaymentData = async () => {
  const [people, jobsSnap, occSnap, payments] = await Promise.all([
    loadPeople(true),
    getDocs(collection(db, "jobs")),
    getDocs(collection(db, "recurring_job_occurrences")).catch(() => ({ docs: [] })),
    loadAllSubPayments(),
  ]);
  return {
    people,
    jobs: jobsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    occurrences: occSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    payments,
  };
};

const owedOnLine = (line) =>
  Math.round(
    (num(line?.basePay) + (Array.isArray(line?.changeOrders) ? line.changeOrders : []).reduce((s, co) => s + num(co?.amount), 0)) * 100
  ) / 100;

const dayOf = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (v?.toDate) return v.toDate().toISOString().slice(0, 10);
  if (v?.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return "";
};

/**
 * Subs who are owed on at least one job: [{ key, name, owed, paid, balance, jobCount }]
 * bucket(name) groups every name form of one person (people.bucketKeyForName).
 */
export const splitPayeeOptions = ({ jobs = [], occurrences = [], payments = [] }, bucket) => {
  const by = new Map();
  const add = (name, owed) => {
    const k = bucket(name);
    if (!k) return;
    const cur = by.get(k) || { key: k, name, owed: 0, paid: 0, jobCount: 0 };
    cur.owed += owed;
    cur.jobCount += 1;
    by.set(k, cur);
  };
  jobs.forEach((j) => (Array.isArray(j.subcontractorPay) ? j.subcontractorPay : []).forEach((l) => owedOnLine(l) > 0 && add(l.subcontractorName || l.name || "", owedOnLine(l))));
  occurrences.forEach((o) => (Array.isArray(o.subcontractorPay) ? o.subcontractorPay : []).forEach((l) => owedOnLine(l) > 0 && add(l.subcontractorName || l.name || "", owedOnLine(l))));
  payments.forEach((p) => {
    const cur = by.get(bucket(p.subcontractorName));
    if (cur) cur.paid += num(p.amount);
  });
  return Array.from(by.values())
    .map((x) => ({ ...x, owed: Math.round(x.owed * 100) / 100, paid: Math.round(x.paid * 100) / 100, balance: Math.round((x.owed - x.paid) * 100) / 100 }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * One row per job (or recurring visit) this sub is owed on:
 * [{ key, jobId, occurrenceKey, date, wo, siteName, label, owed, paid, balance }]
 * Paid = payments to this sub tied to that job / visit.
 */
export const splitRowsForPayee = ({ jobs = [], occurrences = [], payments = [] }, bucket, payeeKey) => {
  const jobsById = {};
  jobs.forEach((j) => {
    jobsById[j.id] = j;
  });
  const rows = new Map();
  const addLine = (key, base, line) => {
    if (bucket(line.subcontractorName || line.name || "") !== payeeKey) return;
    const owed = owedOnLine(line);
    const cur = rows.get(key) || { ...base, key, owed: 0, paid: 0 };
    cur.owed += owed;
    rows.set(key, cur);
  };
  jobs.forEach((j) =>
    (Array.isArray(j.subcontractorPay) ? j.subcontractorPay : []).forEach((l) =>
      addLine(`job:${j.id}`, { jobId: j.id, occurrenceKey: "", date: dayOf(j.startDate), wo: j.wo || "", siteName: j.siteName || "", status: j.status || "" }, l)
    )
  );
  occurrences.forEach((o) => {
    const j = jobsById[o.originalJobId] || {};
    (Array.isArray(o.subcontractorPay) ? o.subcontractorPay : []).forEach((l) =>
      addLine(
        `occ:${o.id}`,
        { jobId: o.originalJobId || "", occurrenceKey: o.id, date: o.occurrenceDate || dayOf(j.startDate), wo: j.wo || "", siteName: j.siteName || "", status: o.status || j.status || "" },
        l
      )
    );
  });
  payments.forEach((p) => {
    if (bucket(p.subcontractorName) !== payeeKey) return;
    const key = p.occurrenceKey ? `occ:${p.occurrenceKey}` : p.jobId ? `job:${p.jobId}` : "";
    const row = key && rows.get(key);
    if (row) row.paid += num(p.amount);
  });
  return Array.from(rows.values()).map((r) => {
    const owed = Math.round(r.owed * 100) / 100;
    const paid = Math.round(r.paid * 100) / 100;
    return {
      ...r,
      owed,
      paid,
      balance: Math.round((owed - paid) * 100) / 100,
      label: `${r.wo ? `WO ${r.wo}` : "No WO"} / ${r.siteName || "Unnamed job"}${r.occurrenceKey ? ` (${r.date})` : ""}`,
    };
  });
};

/** Fill the check oldest-first: each picked row gets up to its balance. Returns { [key]: amount }. */
export const splitOldestFirst = (rows, total) => {
  let left = Math.round(num(total) * 100);
  const out = {};
  [...rows]
    .sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(a.wo).localeCompare(String(b.wo)))
    .forEach((r) => {
      const bal = Math.max(0, Math.round(r.balance * 100));
      const take = Math.min(bal, left);
      out[r.key] = take / 100;
      left -= take;
    });
  return out;
};

/** Divide the check evenly across the picked rows, to the penny (first rows get leftover cents). */
export const splitEqually = (rows, total) => {
  const cents = Math.round(num(total) * 100);
  const n = rows.length;
  if (!n) return {};
  const base = Math.floor(cents / n);
  const rem = cents - base * n;
  const out = {};
  rows.forEach((r, i) => {
    out[r.key] = (base + (i < rem ? 1 : 0)) / 100;
  });
  return out;
};

/**
 * Save one payment per job (and an optional "not tied to a job" remainder),
 * all with the same check # / date and a shared splitGroupId, in
 * "subcontractorPay" + a "transactions" mirror - the records Contractor Pay,
 * the spreadsheet, Sub Pay and the dashboards already read.
 *   allocations [{ jobId, occurrenceKey, jobLabel, amount }]
 * Returns the saved payments.
 */
export const recordSplitSubPayment = async ({ person, allocations, date, method, reference, note, sourcePage = "SplitSubPayment" }) => {
  const day = date || todayKey();
  const groupId = `split_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  const parts = allocations.filter((a) => Math.round(num(a.amount) * 100) > 0);
  const total = Math.round(parts.reduce((s, a) => s + num(a.amount), 0) * 100) / 100;
  const saved = [];
  for (const a of parts) {
    const value = Math.round(num(a.amount) * 100) / 100;
    const payload = {
      subcontractorId: String(person.id || person.name),
      userId: person.userId || "",
      subcontractorName: person.name,
      userName: person.name,
      vendor: person.name,
      property: person.name,
      amount: value,
      amountPaid: value,
      date: day,
      datePaid: new Date(`${day}T12:00:00`),
      method: method || "Check",
      reference: reference || "",
      note: note || `Split payment ${reference ? `#${reference} ` : ""}(${parts.length} parts, total ${total.toFixed(2)})`,
      jobId: a.jobId || "",
      occurrenceKey: a.occurrenceKey || "",
      jobLabel: a.jobLabel || "",
      payFrequency: "once",
      splitGroupId: groupId,
      splitTotal: total,
      splitCount: parts.length,
      category: "Subcontractor",
      effect: "subtract",
      sourcePage,
      createdAt: new Date().toISOString(),
    };
    const refDoc = await addDoc(collection(db, "subcontractorPay"), payload);
    const txRef = await addDoc(collection(db, "transactions"), {
      date: day,
      type: "expense",
      amount: value,
      category: "Subcontractor",
      vendor: person.name,
      memo: `Subcontractor payment${reference ? ` - ${reference}` : ""}${a.jobLabel ? ` (${a.jobLabel})` : " (not tied to a job)"} - split`,
      jobId: a.jobId || null,
      method: payload.method,
      sourceCollection: "subcontractorPay",
      sourceId: refDoc.id,
      updatedAt: new Date(),
    });
    await updateDoc(doc(db, "subcontractorPay", refDoc.id), { txId: txRef.id });
    saved.push({ id: refDoc.id, sourceCollection: "subcontractorPay", txId: txRef.id, ...payload });
  }
  return saved;
};
